import { FormLayout, Modal, TextField, Form, Checkbox, Select, SkeletonBodyText } from '@shopify/polaris'; // 1. Import 'Select'
import { useEffect, useState } from 'react';

import helpers from 'helpers';

import { lengthLessThan, notEmpty, useField, useForm } from '@shopify/react-form';

import __ from 'languages/index';
import { TypedJourney, useMutateCreateJourney, useMutateGetJourney, useUpdateJourney } from 'queries/journey.query';
import TextFieldRichText from 'components/TextFieldRichText';
import { useGetSeatings } from 'queries/seating.query';
const provinceData = require('config/provinceData.json');

/**
 * Create upload Modal for Notification
 */

// eslint-disable-next-line import/no-anonymous-default-export

export default function JourneyModal({ onClose, show, currentVehicleID }: { onClose: Function; show: boolean; currentVehicleID?: bigint }) {
  const { mutateAsync: createEntity, isPending: creating, isSuccess: creatingSuccess } = useMutateCreateJourney();
  const { mutateAsync: updateEntity, isPending: updating, isSuccess: updateSuccess } = useUpdateJourney();
  const { mutateAsync: getVehicle, data: vehicleData } = useMutateGetJourney();

  const [entity, setEntity] = useState<TypedJourney>(null);

  const [seatingOptions, setSeatingOptions] = useState([]);
  const { data, isLoading: seatingLoading } = useGetSeatings({
    limit: 20,
    sort: 'createdAt:desc',
  });

  useEffect(() => {
    if (!helpers.isEmpty(data)) {
      let { body } = data;
      let totalResult = [
        {
          label: 'Không chọn',
          value: '',
        },
      ];
      for (let seating of body) {
        totalResult.push({
          label: seating.seating_name,
          value: seating.id,
        });
      }

      setSeatingOptions(totalResult);
    }
  }, [data]);

  // 2. Chuyển đổi JSON thành options cho component Select
  const provinceOptions = [
    { label: 'Chọn một địa điểm', value: '' }, // Thêm một lựa chọn mặc định
    ...Object.entries(provinceData).map(([id, name]) => ({
      label: String(name),
      value: String(id),
    })),
  ];

  useEffect(() => {
    if (currentVehicleID) {
      getVehicle(currentVehicleID);
    } else {
      setEntity(null);
    }
  }, [currentVehicleID]);

  const useFields = {
    journey_name: useField<string>({
      value: entity?.journey_name ?? '',
      validates: [notEmpty('Không được để trống trường này.'), lengthLessThan(250, 'No more than 250 characters.')],
    }),

    journey_description: useField<string>({
      value: entity?.journey_description ?? '',
      validates: [notEmpty('Không được để trống trường này.'), lengthLessThan(65000, 'No more than 65000 characters.')],
    }),

    journey_default_price: useField<number>({
      value: entity?.journey_default_price ?? 1,
      validates: [
        (inputVal) => {
          if (!helpers.isNumber(inputVal)) return 'Phải là một số hợp lệ.';
        },
      ],
    }),
    journey_total_time: useField<string>({
      value: String(entity?.journey_total_time ?? 0),
      validates: [
        (inputVal) => {
          if (!helpers.isNumber(inputVal)) return 'Phải là một số hợp lệ.';
        },
      ],
    }),
    journey_distance: useField<number>({
      value: Number(entity?.journey_distance ?? 1),
      validates: [
        (inputVal) => {
          if (!helpers.isNumber(inputVal)) return 'Phải là một số hợp lệ.';
        },
      ],
    }),

    // journey_from vẫn giữ lại để lưu tên tỉnh thành (ví dụ: "Thành Phố Hà Nội")
    journey_from: useField<string>({
      value: entity?.journey_from ?? '',
      validates: [notEmpty('Vui lòng chọn điểm đi.')],
    }),

    // journey_to vẫn giữ lại để lưu tên tỉnh thành (ví dụ: "Thành Phố Hải Dương")
    journey_to: useField<string>({
      value: entity?.journey_to ?? '',
      validates: [notEmpty('Vui lòng chọn điểm đến.')],
    }),

    // 3. Cập nhật useField cho các trường _geo để lưu ID
    // Các validate không cần thiết nữa vì người dùng chọn từ danh sách có sẵn
    journey_from_geo: useField<string>({
      value: entity?.journey_from_geo ?? '',
      validates: [notEmpty('Vui lòng chọn điểm đi.')],
    }),

    journey_to_geo: useField<string>({
      value: entity?.journey_to_geo ?? '',
      validates: [notEmpty('Vui lòng chọn điểm đến.')],
    }),

    journey_default_seating: useField<string>({ value: entity?.journey_default_seating, validates: [] }),
    is_default: useField<number>({ value: entity?.is_default, validates: [] }),

    journey_status: useField<number>({
      value: entity?.journey_status ?? 1,
      validates: [
        (inputVal) => {
          if (!helpers.isNumber(inputVal)) return 'Phải là một số hợp lệ.';
        },
      ],
    }),
  };

  const {
    fields,
    submit,
    dirty,
    reset: resetForm,
  } = useForm({
    fields: useFields,

    async onSubmit(values) {
      try {
        // create new
        if (!currentVehicleID) {
          await createEntity({
            journey_name: values.journey_name,
            journey_description: values.journey_description,
            journey_from: values.journey_from,
            journey_from_geo: values.journey_from_geo,
            journey_to: values.journey_to,
            journey_to_geo: values.journey_to_geo,
            journey_total_time: helpers.parseNumeric(values.journey_total_time),
            journey_distance: helpers.parseNumeric(values.journey_distance),
            journey_default_price: helpers.parseNumeric(values.journey_default_price),
            journey_default_seating: values.journey_default_seating || null,
            journey_status: values.journey_status,
            is_default: values.is_default,
            journey_parent: BigInt('0'),
          });
        } else {
          await updateEntity({
            id: String(currentVehicleID),
            journey_name: values.journey_name,
            journey_description: values.journey_description,
            journey_from: values.journey_from,
            journey_from_geo: values.journey_from_geo,
            journey_to: values.journey_to,
            journey_to_geo: values.journey_to_geo,
            journey_total_time: helpers.parseNumeric(values.journey_total_time),
            journey_distance: helpers.parseNumeric(values.journey_distance),
            journey_default_price: helpers.parseNumeric(values.journey_default_price),
            journey_default_seating: values.journey_default_seating,
            journey_status: values.journey_status,
            is_default: values.is_default,
            journey_parent: BigInt('0'),
          });
        }
      } catch (e: any) {
        if (e.params !== undefined) {
          if (e.params.field !== undefined) {
            useFields[e.params.field].setError(__('check_this_field_again'));
          }
        }
      }
      return { status: 'success' };
    },
  });
  /* End form */

  useEffect(() => {
    if (vehicleData) {
      setEntity(vehicleData); // Giữ lại để các phần khác của component có thể dùng

      // Dùng hàm resetForm để cập nhật toàn bộ giá trị cho form
      // Đây là bước quan trọng nhất để sửa lỗi
      resetForm();
    }
  }, [vehicleData, resetForm]); // Thêm resetForm vào dependency array

  useEffect(() => {
    if (updateSuccess || creatingSuccess) {
      resetForm();
      onClose();
    }
  }, [updateSuccess, creatingSuccess]);

  useEffect(() => {
    if (!currentVehicleID) {
      resetForm();
      setEntity(null);
    }
  }, [currentVehicleID]);

  const handleProvinceChange = (
    selectedId: string,
    fieldName: 'journey_from' | 'journey_to',
    fieldGeoName: 'journey_from_geo' | 'journey_to_geo'
  ) => {
    const selectedLabel = provinceData[selectedId] || '';
    fields[fieldGeoName].onChange(selectedId);
    fields[fieldName].onChange(selectedLabel);
  };

  const Actual_page = (
    <Modal
      open={show}
      onClose={() => onClose()}
      title={currentVehicleID ? 'Cập nhật hành trình' : 'Tạo hành trình'}
      primaryAction={{
        content: currentVehicleID ? 'Cập nhật' : 'Tạo',
        disabled: !dirty,
        loading: updating || creating,
        onAction: submit,
      }}
      secondaryActions={[
        {
          content: 'Close',
          onAction: () => onClose(),
        },
      ]}
    >
      <Modal.Section>
        <Form onSubmit={submit}>
          <FormLayout>
            <TextField autoFocus autoComplete="off" requiredIndicator label="Tên hành trình" {...fields.journey_name} />
            <FormLayout.Group condensed>
              <TextField
                label="Tổng số phút thực hiện (Phút)"
                type="number"
                autoComplete="off"
                {...fields.journey_total_time}
                helpText={fields.journey_total_time.value ? `Tương đương ${helpers.minuteToFullHourString(fields.journey_total_time.value)}` : ''}
              />
              <TextField
                label="Khoảng cách (KM)"
                autoComplete="off"
                {...fields.journey_distance}
                value={helpers.formatNumberWithCommas(fields.journey_distance.value)}
                onChange={(a) => fields.journey_distance.onChange(helpers.parseNumeric(a))}
              />
              <TextField
                label="Giá mặc định (VND)"
                autoComplete="off"
                {...fields.journey_default_price}
                value={helpers.formatNumberWithCommas(fields.journey_default_price.value)}
                onChange={(a) => fields.journey_default_price.onChange(helpers.parseNumeric(a))}
              />
            </FormLayout.Group>

            {/* 4. Thay thế TextField bằng Select */}
            {seatingLoading ? (
              <SkeletonBodyText />
            ) : (
              <Select
                label="Loại hình vận chuyển?"
                requiredIndicator
                options={seatingOptions}
                onChange={(selectedId) => {
                  fields.journey_default_seating.onChange(selectedId);
                }}
                value={fields.journey_default_seating.value} // Giá trị của Select là ID (lấy từ trường _geo)
                error={fields.journey_default_seating.error} // Hiển thị lỗi từ trường _geo
              />
            )}

            {/* 4. Thay thế TextField bằng Select */}
            <Select
              label="Từ đâu?"
              requiredIndicator
              options={provinceOptions}
              onChange={(selectedId) => handleProvinceChange(selectedId, 'journey_from', 'journey_from_geo')}
              value={fields.journey_from_geo.value} // Giá trị của Select là ID (lấy từ trường _geo)
              error={fields.journey_from_geo.error} // Hiển thị lỗi từ trường _geo
            />

            <Select
              label="Đến đâu?"
              requiredIndicator
              options={provinceOptions}
              onChange={(selectedId) => handleProvinceChange(selectedId, 'journey_to', 'journey_to_geo')}
              value={fields.journey_to_geo.value} // Giá trị của Select là ID (lấy từ trường _geo)
              error={fields.journey_to_geo.error} // Hiển thị lỗi từ trường _geo
            />

            <TextFieldRichText
              label={'Mô tả'}
              initialData={fields.journey_description.value}
              onCallback={(v) => fields.journey_description.onChange(v)}
              error={fields.journey_description.error}
            />

            <Checkbox
              label="Đánh dấu là hành trình mặc định?"
              checked={Boolean(fields.is_default.value === 1)}
              onChange={(e) => fields.is_default.onChange(Number(e))}
            />
            <Checkbox
              label="Hoạt động"
              checked={Boolean(fields.journey_status.value === 1)}
              onChange={(e) => fields.journey_status.onChange(Number(e))}
            />
          </FormLayout>
        </Form>
      </Modal.Section>
    </Modal>
  );

  return <>{Actual_page}</>;
}

